/**
 * 建任务 —— 直接写台账(POST /ledger/task)。
 *
 * 原型这里有一步"AI 已将目标自动分解为 4 个工作单元",单元名是把目标字符串
 * 拼上「区域A/B/C」。那一步在 hub 上**不存在**且是刻意的:
 *   `handleCreateTask` 明写「units required(hub v0 不做自动分解,那依赖边缘的探索器)」——
 *   怎么切区域取决于地图、覆盖度、狗的位置,这些只有边缘知道。云端拍脑袋切出来的
 *   格子会被设备当成真任务去跑。
 * 所以这里让人**自己定工作单元**,并把这条理由写在界面上。
 *
 * 另外两个原型字段(优先级、截止时间)台账里没有对应列,填了会被丢掉 ——
 * 与其留个输入框骗人,不如不放。
 */
import { useState } from 'react';
import { hub, HubError, type UnitSpec } from '@/lib/hubClient';

interface Props { open: boolean; onClose: () => void; onCreated: () => void; }

interface DraftUnit { label: string; kind: string; leaseSec: number }

const KIND_HINTS = ['inspect', 'explore', 'find_object', 'deliver', 'custom'];

export default function CreateTaskModal({ open, onClose, onCreated }: Props) {
  const [goal, setGoal] = useState('');
  const [kind, setKind] = useState('custom');
  const [mapId, setMapId] = useState('');
  const [units, setUnits] = useState<DraftUnit[]>([{ label: '', kind: 'custom', leaseSec: 60 }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const valid = goal.trim() !== '' && units.some((u) => u.label.trim() !== '');

  const reset = () => { setGoal(''); setKind('custom'); setMapId(''); setUnits([{ label: '', kind: 'custom', leaseSec: 60 }]); setError(''); };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setError('');
    const specs: UnitSpec[] = units
      .filter((u) => u.label.trim() !== '')
      .map((u, i) => ({
        kind: u.kind || 'custom',
        label: u.label.trim(),
        seq: i + 1,
        map_id: mapId.trim() || undefined,
        lease_ms: Math.max(10, u.leaseSec) * 1000,
      }));
    try {
      await hub.createTask({ goal: goal.trim(), kind, mapId: mapId.trim() || undefined, units: specs });
      onCreated();
      close();
    } catch (e) {
      setError(e instanceof HubError && e.status === 403
        ? '仅座舱(admin 令牌)可建任务 —— 设备只能认领,不能派活给别人'
        : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const setUnit = (i: number, patch: Partial<DraftUnit>) =>
    setUnits((prev) => prev.map((u, j) => (j === i ? { ...u, ...patch } : u)));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 animate-backdrop-in" onClick={close}></div>
      <div className="relative bg-background-50 rounded-t-xl sm:rounded-xl border border-background-200/70 w-full sm:max-w-lg max-h-[88vh] overflow-hidden flex flex-col animate-fade-in-up">
        <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
          <h2 className="text-sm font-bold text-foreground-900">创建任务</h2>
          <button onClick={close} className="w-9 h-9 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-[11px] font-medium text-foreground-700 mb-1.5">任务目标</label>
            <textarea value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="例：把三楼检查一遍" className="w-full h-20 px-3 py-2 text-xs bg-background-50 border border-background-200/70 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-foreground-700 mb-1.5">任务类别</label>
              <input list="task-kinds" value={kind} onChange={(e) => setKind(e.target.value)} className="w-full px-2.5 py-2 text-xs font-mono bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400" />
              <datalist id="task-kinds">{KIND_HINTS.map((k) => <option key={k} value={k} />)}</datalist>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-foreground-700 mb-1.5">地图 ID <span className="text-foreground-400 font-normal">(可空)</span></label>
              <input value={mapId} onChange={(e) => setMapId(e.target.value)} placeholder="map_xxx" className="w-full px-2.5 py-2 text-xs font-mono bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-medium text-foreground-700">工作单元</label>
              <button onClick={() => setUnits((p) => [...p, { label: '', kind: kind || 'custom', leaseSec: 60 }])} className="text-[11px] text-primary-600 font-medium cursor-pointer whitespace-nowrap">+ 添加单元</button>
            </div>
            <p className="text-[10px] text-foreground-400 mb-2 leading-relaxed">
              hub 不做自动分解:怎么切取决于地图、覆盖度和设备位置,只有边缘知道。
              云端拍脑袋切出来的格子,设备会当成真任务去跑。
            </p>
            <div className="space-y-2">
              {units.map((u, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 h-5 shrink-0 rounded-full bg-background-100 text-foreground-500 text-[10px] flex items-center justify-center font-mono">{i + 1}</span>
                  <input value={u.label} onChange={(e) => setUnit(i, { label: e.target.value })} placeholder="单元描述,例:三楼东区" className="flex-1 min-w-0 px-2.5 py-2 text-xs bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400" />
                  <input value={u.leaseSec} onChange={(e) => setUnit(i, { leaseSec: Number(e.target.value) || 0 })} type="number" min={10} title="租约秒数:超时未心跳则单元被回收" className="w-16 shrink-0 px-2 py-2 text-xs font-mono bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400" />
                  <button onClick={() => setUnits((p) => p.filter((_, j) => j !== i))} disabled={units.length === 1} className="w-9 h-9 shrink-0 flex items-center justify-center rounded hover:bg-background-100 disabled:opacity-30 cursor-pointer"><i className="ri-delete-bin-line text-sm text-foreground-400" /></button>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-foreground-400 mt-1.5">右侧数字是租约秒数 —— 认领后这么久没心跳,单元会被回收给别人(死终端不能锁死工作)。</p>
          </div>

          {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200/70 rounded-lg px-3 py-2">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-background-200/70">
          <button onClick={close} className="px-3 py-2 text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap">取消</button>
          <button onClick={submit} disabled={!valid || busy} className={`px-4 py-2 text-xs font-medium rounded-md whitespace-nowrap cursor-pointer transition-colors ${!valid || busy ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'}`}>{busy ? '写入台账…' : '发布任务'}</button>
        </div>
      </div>
    </div>
  );
}
